<template>
  <div>
    <div class="tree-menu-field has-action">
      <a class="tree-menu-field-action" @click="onClick(category.id)">
        <b-icon
          :icon="
            isOpen && $_.get(category, 'subcategories', []).length
              ? 'chevron-down'
              : 'chevron-right'
          "
          size="is-small"
          :class="{
            'has-text-dark':
              (searchedValue.length &&
                isOpen &&
                $_.get(category, 'isSearched')) ||
              (!searchedValue.length && isOpen),
            'has-text-grey-lighter':
              !isOpen ||
              (searchedValue.length && !$_.get(category, 'isSearched'))
          }"
        />
      </a>
      <div class="tree-menu-field-text" @click="onClick(category.id)">
        <p
          class="trim-long-value"
          :class="{
            'is-selected':
              (searchedValue.length &&
                isOpen &&
                $_.get(category, 'isSearched')) ||
              (!searchedValue.length && isOpen)
          }"
        >
          <!-- XSS check: getCategoryName is sanitized -->
          <span v-html="getCategoryName()" />
          <b-tag
            v-if="$_.get(category, 'products_count', 0)"
            class="has-margin-left-25"
            type="is-grey-light"
            rounded
          >
            <strong class="has-text-grey">{{
              $_.get(category, "products_count")
            }}</strong>
          </b-tag>
        </p>
      </div>
    </div>

    <div v-if="isOpen" class="tree-menu-field-container">
      <tree-menu-field
        v-for="subcategory in $_.get(category, 'subcategories', [])"
        :key="`category-${subcategory.id}`"
        :category="subcategory"
        :searched-value="searchedValue"
        @click="onClick"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dompurify from "dompurify";
import type { PropType } from "vue";

export default defineComponent({
  name: "TreeMenuField",
  props: {
    category: { type: Object as PropType<object>, required: false },
    searchedValue: { type: String, default: "" }
  },
  computed: {
    isOpen(): boolean {
      return this.$_.get(this.category, "isOpen", false);
    }
  },
  methods: {
    onClick(categoryId) {
      this.$emit("click", categoryId);
    },
    getCategoryName() {
      const name: string = this.$_.get(
        this.category,
        "name_translated",
        this.$_.get(this.category, "name")
      );
      return `${this.getHighlighted(
        dompurify.sanitize(name),
        this.searchedValue
      )}`;
    },
    getHighlighted(oldString: string, searchedString: string): string {
      const index = oldString
        .toLowerCase()
        .indexOf(searchedString.toLowerCase());
      if (index === 0 && searchedString.length) {
        return `<span class="has-text-primary">${oldString.substr(
          0,
          searchedString.length
        )}</span>${
          oldString.length === searchedString.length
            ? ""
            : this.getHighlighted(
                oldString.substr(searchedString.length, oldString.length - 1),
                searchedString
              )
        }`;
      }

      if (index > 0) {
        return `${oldString.substr(
          0,
          index
        )}<span class="has-text-primary">${oldString.substr(
          index,
          searchedString.length
        )}</span>${this.getHighlighted(
          oldString.substr(index + searchedString.length, oldString.length - 1),
          searchedString
        )}`;
      }

      return oldString;
    }
  }
});
</script>

<style lang="scss" scoped>
.tree-menu-field {
  padding: 0.25rem 0.5rem;
  display: grid;
  grid-template-columns: 1em auto;
  grid-column-gap: 0.25rem;
  align-items: center;
  transition: padding ease-out 0.2s;

  .tree-menu-field-text {
    grid-row: 2;
    grid-column: 2;
    overflow: hidden;
    transition: opacity ease-out 0.2s;

    p.trim-long-value {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      color: $dark;

      &.is-selected {
        font-weight: 500;
      }
    }
  }

  a.tree-menu-field-action {
    grid-row: 2;
    grid-column: 1;
    color: $dark;

    :first-child {
      display: inline;
    }

    & > :first-child {
      margin-right: 0.15rem;
    }
  }
}

.tree-menu-field.has-action {
  cursor: pointer;
}

.tree-menu-field-container {
  padding-left: 1.25em;
  margin-bottom: 0.125em;
}
</style>
